import type { Feature, FeatureCollection, LineString, Position } from "geojson";
import type { RowKey } from "./stopPlanning";
import { parseRowKey } from "./stopPlanning";
import type { Dropoff, ShiftDropoff } from "@domain/CRM/dropoff";
import { PullInPoint } from "@domain/CRM/job";
import { buildShiftRows, shiftRowKey } from "./shiftStops";

/**
 * Route lines: connect a shift's stopping points (job pull-ins + dropoff stops) in run
 * order — the port of MC's "order lines" (design `scheduling-route-lines-design.md`).
 * The caller resolves row keys to coordinates and simply OMITS unlocatable stops, so
 * consecutive located stops connect — exactly MC's `lastJob` skip-and-bridge walk.
 */

export interface ShiftRouteInput {
	shiftId: string;
	robotName: string;
	/** The shift's located stop points, already in run order. */
	points: Position[];
}

export type ShiftRouteProps = { shiftId: string; robotName: string; color: string };
export type ShiftRouteFC = FeatureCollection<LineString, ShiftRouteProps>;

const EMPTY_ROUTE_FC: ShiftRouteFC = { type: "FeatureCollection", features: [] };

/** Parse a `Jobs."Location"` pull-in string ("lat,lng") into GeoJSON [lng, lat].
 *  The rules live on the {@link PullInPoint} value object; this keeps the call-site shape the
 *  route resolver already uses (a nullable Position, not a domain object). */
export function parseLatLngLocation(location: string | undefined): Position | null {
	return PullInPoint.parse(location)?.toPosition() ?? null;
}

/** Deterministic robot-name → color for route strokes. MC's style intends
 *  `generateColor(robot)` (its call site accidentally hashes the sched date — quirk
 *  fixed, not ported). Hash → hue keeps saturation/lightness readable on satellite. */
export function robotRouteColor(robotName: string): string {
	let hash = 0;
	for (let i = 0; i < robotName.length; i++) {
		hash = (hash * 31 + robotName.charCodeAt(i)) | 0;
	}
	const hue = ((hash % 360) + 360) % 360;
	return `hsl(${hue}, 85%, 45%)`;
}

/** Identity color of a shift with no robot attached ("scheduled green" — what every scheduled
 *  job wore before shifts took their robot's color). */
export const NO_ROBOT_SHIFT_COLOR = "#2e7d32";

/** A shift's identity color on the scheduling board: its robot's route color, so the shift's
 *  jobs, dropoff stops, route line and card all read as one thing. */
export function shiftIdentityColor(robotName: string | null | undefined): string {
	return robotName ? robotRouteColor(robotName) : NO_ROBOT_SHIFT_COLOR;
}

export interface RoutePointLookups {
	/** jobId → `Jobs."Location"` pull-in string (parsed here; wins over the centroid). */
	jobLocationById: ReadonlyMap<string, string | undefined>;
	/** jobId → boundary-center fallback for jobs without a (valid) Location. */
	jobCentroidById: ReadonlyMap<string, Position>;
	/** committed stop id → its resolved point (junction lookups / master fallback). */
	stopPointByStopId: ReadonlyMap<string, Position>;
	/** master dropoff id → point (locates staged `newstop:` rows). */
	dropoffPointById: ReadonlyMap<string, Position>;
	/** staged newstop temp key → its master dropoff id. */
	newStopDropoffIdByKey: ReadonlyMap<string, string>;
}

/** Locate a shift's mixed row-key sequence, in run order. Unlocatable rows are OMITTED,
 *  so consecutive located stops connect — MC's `lastJob` skip-and-bridge walk. */
export function resolveRoutePoints(rowKeys: readonly RowKey[], lookups: RoutePointLookups): Position[] {
	const points: Position[] = [];
	for (const key of rowKeys) {
		const row = parseRowKey(key);
		let point: Position | null | undefined;
		if (row.kind === "job") {
			point = parseLatLngLocation(lookups.jobLocationById.get(row.id)) ?? lookups.jobCentroidById.get(row.id);
		} else if (row.kind === "stop") {
			point = lookups.stopPointByStopId.get(row.id);
		} else {
			const dropoffId = lookups.newStopDropoffIdByKey.get(row.id);
			point = dropoffId ? lookups.dropoffPointById.get(dropoffId) : undefined;
		}
		if (point) points.push(point);
	}
	return points;
}

/** One LineString per shift through its located stops in run order (MC emits per-hop
 *  2-point segments — an incremental-walk artifact; identical pixels). Shifts with
 *  fewer than two located stops draw nothing. */
export function buildShiftRouteFC(routes: readonly ShiftRouteInput[]): ShiftRouteFC {
	const features: Feature<LineString, ShiftRouteProps>[] = [];
	for (const route of routes) {
		if (route.points.length < 2) continue;
		features.push({
			type: "Feature",
			geometry: { type: "LineString", coordinates: route.points },
			// Shift identity color, not a raw hash: a robot-less shift's line matches its green jobs,
			// stop rings and card dot instead of hashing "" to hue 0 (red — an alert color here).
			properties: { shiftId: route.shiftId, robotName: route.robotName, color: shiftIdentityColor(route.robotName) },
		});
	}
	return features.length === 0 ? EMPTY_ROUTE_FC : { type: "FeatureCollection", features };
}

/** Everything {@link buildSelectedShiftRouteFC} needs to locate one shift's stops. */
export interface SelectedShiftRouteInput {
	/** The shift being routed. */
	shift: { id: string; jobIds: readonly string[]; robotName?: string };
	/** This shift's committed dropoff stops, in the order they sit on the shift. */
	stops: readonly ShiftDropoff[];
	/** Every loaded shift's stops. Points resolve from this wider set because a row key can
	 *  reference a stop that isn't among `stops`. */
	allStops: readonly ShiftDropoff[];
	/** Master dropoffs — a stop without its own lat/lng inherits the master's. */
	dropoffs: readonly Dropoff[];
	/** The shift's own jobs, then any other loaded jobs — first source to know an id wins. */
	shiftJobsById: ReadonlyMap<string, JobLocation>;
	otherJobSources: ReadonlyArray<readonly JobLocation[]>;
	/** jobId → boundary-center fallback for jobs without a (valid) Location. */
	jobCentroidById: ReadonlyMap<string, Position>;
	/** jobId → a pull-in point staged on the scheduling board but not yet saved. Wins over the
	 *  job's committed `location`, so the route line snaps to a newly placed cluster pull-in
	 *  immediately rather than after Save. */
	stagedJobLocationById?: ReadonlyMap<string, string>;
	/** Staged newstop temp key → its master dropoff id. */
	stagedNewStops: ReadonlyMap<string, { dropoffId: string }>;
	/** The card's displayed mixed row order, when the shift has been touched. Derived from
	 *  the shift's jobs and stops otherwise. */
	displayRowKeys?: readonly RowKey[];
}

export interface JobLocation {
	id: string;
	location?: string;
	order?: number;
}

/**
 * The route line for the one shift the user has selected. Resolves each stop's
 * point from the layered lookups ({@link RoutePointLookups}) and hands the
 * located sequence to {@link buildShiftRouteFC}.
 */
export function buildSelectedShiftRouteFC(input: SelectedShiftRouteInput): ShiftRouteFC {
	const { shift, stops, allStops, dropoffs, shiftJobsById, otherJobSources, jobCentroidById, stagedNewStops } = input;

	const rowKeys =
		input.displayRowKeys ??
		buildShiftRows(shift.jobIds, new Map(shift.jobIds.map((id) => [id, shiftJobsById.get(id)?.order])), stops).map(
			shiftRowKey,
		);

	const jobLocationById = new Map<string, string | undefined>();
	for (const [id, job] of shiftJobsById) jobLocationById.set(id, job.location);
	for (const source of otherJobSources) {
		for (const j of source) if (!jobLocationById.has(j.id)) jobLocationById.set(j.id, j.location);
	}
	// Staged points are the last word: they are what the user just placed, and they may name a
	// job that has no committed location at all (so `set`, not a conditional overwrite).
	if (input.stagedJobLocationById) {
		for (const [id, location] of input.stagedJobLocationById) jobLocationById.set(id, location);
	}

	const dropoffsById = new Map(dropoffs.map((d) => [d.id, d]));
	const stopPointByStopId = new Map<string, Position>();
	for (const stop of allStops) {
		const master = stop.dropoffId ? dropoffsById.get(stop.dropoffId) : undefined;
		const lat = stop.latitude ?? master?.latitude;
		const lng = stop.longitude ?? master?.longitude;
		if (lat !== undefined && lng !== undefined) stopPointByStopId.set(stop.id, [lng, lat]);
	}

	const dropoffPointById = new Map<string, Position>();
	for (const d of dropoffs) {
		if (d.latitude !== undefined && d.longitude !== undefined) dropoffPointById.set(d.id, [d.longitude, d.latitude]);
	}

	const newStopDropoffIdByKey = new Map<string, string>();
	for (const [key, meta] of stagedNewStops) newStopDropoffIdByKey.set(key, meta.dropoffId);

	const points = resolveRoutePoints(rowKeys, {
		jobLocationById,
		jobCentroidById,
		stopPointByStopId,
		dropoffPointById,
		newStopDropoffIdByKey,
	});
	return buildShiftRouteFC([{ shiftId: shift.id, robotName: shift.robotName ?? "", points }]);
}
